/**
 * The web app's two announcements to the iOS host about a tool window's life:
 * that one is on screen, and that one finished its job.
 *
 * **Chrome suppression.** The host installs the Command Center logo button as a
 * window-level overlay anchored bottom-left, ON TOP of the WKWebView, and — mid
 * tutorial — a coach-mark pill anchored to it. CSS cannot see either, so a
 * web-side inset would be a hardcoded guess at their size and position: wrong on
 * the next device, and wrong again if that button ever moves. Hiding the native
 * chrome for the window's duration is correct at any screen size by construction.
 *
 * The wire name is still `webBottomPanel` because it is a shipped contract on
 * both sides and web and iOS deploy independently — renaming it would silently
 * lose the signal for every new-web/old-iOS pairing. It now carries centred
 * modals as well as the bottom-docked panel it was named for.
 *
 * **Completion.** Web-routed tools report nothing back by default, so the
 * new-user tutorial had to advance on the tile TAP — which is why it
 * congratulated kitchen users on a layout they had not generated yet.
 * `postHostToolCompleted` is that missing signal.
 *
 * Both no-op outside the iOS host, so callers need no platform check.
 */

/**
 * The one command name shared with Swift. Matches
 * `CommandCenterTool.chooseNewLayout.tutorialCommandName` and the
 * `onCommandCompleted` name the native bathroom and dorm flows already emit.
 */
export const HOST_COMMAND_NEW_LAYOUT = 'newLayout';

/**
 * Matches `CommandCenterTool.fixturesAndFinishes.tutorialCommandName`, which is
 * `TutorialCommand.fixturesFinishes.rawValue`. Once the iPhone tile bridges
 * here instead of opening the native picker, the web panel is the only thing
 * that can report this tool was used.
 */
export const HOST_COMMAND_FIXTURES_FINISHES = 'fixturesFinishes';

/** Matches `CommandCenterTool.autoStyle.tutorialCommandName`. */
export const HOST_COMMAND_AUTO_STYLE = 'autoStyle';

/**
 * Saving the room's current design as a reusable package.
 *
 * Distinct from `fixturesFinishes`, which reports that the panel was USED. This
 * reports the one action the contractor tour's packages step is waiting for,
 * and nothing else in the panel produces it.
 */

/**
 * The user put their own price into the takeoff.
 *
 * Not "opened the takeoff" — that is already reported by the modal's own
 * lifecycle. This is the moment a seeded estimate becomes THEIR estimate, and
 * it is the only thing the contractor tour's pricing step waits for.
 */
export const HOST_COMMAND_TAKEOFF_PRICED = 'takeoffPriced';

/**
 * Either takeoff PDF exported successfully — the summary or the detailed one.
 *
 * One command for both, deliberately: the tour's question is "have they got
 * something they can hand over", and both answers are yes. Which of the two
 * they picked is a purchasing decision, not a tutorial one.
 */
export const HOST_COMMAND_TAKEOFF_EXPORTED = 'takeoffExported';

/**
 * Why a tool stopped.
 *
 * `applied` — the user got what they came for.
 * `exhausted` — the tool ran and had nothing to offer. A kitchen no layout fits
 * renders no Apply button at all, so the tour must not sit waiting for one.
 *
 * A plain cancel posts NEITHER: the user can still come back and succeed, and
 * the tutorial's job there is to keep pointing them at the tool.
 */
export const HOST_OUTCOME_APPLIED = 'applied';
export const HOST_OUTCOME_EXHAUSTED = 'exhausted';

/**
 * @param {boolean} open  true while a tool window is showing
 * @returns {boolean} true when the host was notified
 */
export function setHostBottomPanelOpen(
  open,
  chooser = false,
  kind = undefined
) {
  const handler = window.webkit?.messageHandlers?.webBottomPanel;
  if (!handler) return false;
  // `chooser` distinguishes a window that is a STEP on the way — the room
  // picker — from the tool the user was actually sent to. Both stow the host's
  // chrome, and the host cannot tell them apart on its own, so a tutorial
  // lesson drew "tap any surface or fitting to swap it" over a list of rooms
  // and pulsed the picker's ✕ when the right move was to pick a room.
  // `kind` is the three-way answer — 'tool' | 'chooser' | 'widget'. `chooser`
  // is kept beside it, redundantly, because already-shipped hosts read that key
  // and have never heard of this one; they drop `kind` silently (an unknown key
  // in a `postMessage` body is simply not read), so adding it cannot change
  // their behaviour. Hosts that DO read it can tell furniture — an expanded
  // minimap, the elements tray — from the tool a tutorial sent the user into,
  // which `chooser` alone cannot express.
  handler.postMessage({ open: !!open, chooser: !!chooser, kind });
  return true;
}

/**
 * @param {string} command  a host command name, e.g. HOST_COMMAND_NEW_LAYOUT
 * @param {string} outcome  HOST_OUTCOME_APPLIED or HOST_OUTCOME_EXHAUSTED
 * @returns {boolean} true when the host was notified
 */
export function postHostToolCompleted(command, outcome) {
  const handler = window.webkit?.messageHandlers?.webToolCompleted;
  if (!handler || !command || !outcome) return false;
  handler.postMessage({ command, outcome });
  return true;
}

/**
 * Tell the host the user OPENED a design tool from a web control rather than
 * the Command Center — the room properties panel reaches New Layout, Auto Style
 * and Fixtures & Finishes on its own. The host records it so the tutorial
 * library ticks the lesson for that tool however the user got there.
 *
 * @param {string} command  HOST_COMMAND_NEW_LAYOUT / _AUTO_STYLE / _FIXTURES_FINISHES
 * @returns {boolean} true when the host was notified
 */
export function postHostToolOpened(command) {
  const handler = window.webkit?.messageHandlers?.webToolOpened;
  if (!handler || !command) return false;
  handler.postMessage({ command });
  return true;
}

/**
 * Tell the host a web tool window has closed.
 *
 * **Why these live here rather than at the call site.** `scheduleTakeoffClosed`
 * was posted as a raw `window.webkit` reach from ONE of the three places
 * `ScheduleModal` is mounted, so closing the takeoff from the floorplan or
 * elevations tab never reported it and the tutorial beat that only this signal
 * can end sat there forever. Behind a helper, called from inside the modal, a
 * new mount site cannot forget.
 *
 * The wire names stay verbatim. `scheduleTakeoffClosed` is a shipped contract:
 * a rename would post to a handler no released iOS build registers, and the
 * signal would vanish silently on exactly the pairing that needs it most.
 */
function postModalClosed(handlerName) {
  const handler = window.webkit?.messageHandlers?.[handlerName];
  if (!handler) return false;
  handler.postMessage({});
  return true;
}

/** @returns {boolean} true when the host was notified */
export function postScheduleTakeoffClosed() {
  return postModalClosed('scheduleTakeoffClosed');
}

/** @returns {boolean} true when the host was notified */
export function postRecordingsModalClosed() {
  return postModalClosed('recordingsModalClosed');
}

/**
 * The Recordings modal OPENED.
 *
 * The close signal alone is not enough for a beat whose instruction changes
 * once the modal is up: before it, "tap the logo and open Recordings" has to
 * point at the logo; after it, the logo is behind the modal and the pill must
 * float instead. Without this the host cannot tell those apart and one of the
 * two states gets the wrong pill.
 *
 * @returns {boolean} true when the host was notified
 */
export function postRecordingsModalOpened() {
  return postModalClosed('recordingsModalOpened');
}

/**
 * The Schedules & Takeoffs modal OPENED.
 *
 * Paired with `postScheduleTakeoffClosed` for the same reason Recordings has a
 * pair: the host cannot otherwise tell that this modal is up, and a tour beat
 * about something else keeps repainting its instruction — with a glow on a
 * logo the modal is covering — over the prices the user came to read.
 *
 * @returns {boolean} true when the host was notified
 */
export function postScheduleTakeoffOpened() {
  return postModalClosed('scheduleTakeoffOpened');
}

/**
 * A modal's VIEW went away without the user closing it.
 *
 * Deliberately NOT `post…Closed`. The host ACTS on a close — the takeoff's
 * advances the tour and latches that the user has seen it; the renderings
 * dialog's rewinds a step. React tears a subtree down and rebuilds it for
 * reasons that are nothing to do with the user (an error overlay swapping in
 * while a refetch fails, a route change), and reporting those as closes steps
 * the tour under someone who is still looking at the modal.
 *
 * Not React's development double-invoke, despite the resemblance: that fires at
 * MOUNT, where both dialogs are closed, so the ref guarding this is false and
 * nothing is posted.
 *
 * Only the sender knows which happened, so only the sender can say.
 *
 * @param {string} modal  'scheduleTakeoff' or 'renderings'
 * @returns {boolean} true when the host was notified
 */
export function postWebModalDetached(modal) {
  const handler = window.webkit?.messageHandlers?.webModalDetached;
  if (!handler) return false;
  handler.postMessage({ modal });
  return true;
}

export default setHostBottomPanelOpen;
